import React, { useMemo, useState } from 'react';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { AlertTriangle, CheckCircle2, CircleDashed } from 'lucide-react';
import { channelAttributionData, dataQualityLabels, revenueMetricOptions } from './data';

const formatWholeNumber = (num) => {
  if (num === null || num === undefined) return '-';
  return num.toLocaleString();
};

const formatMoney = (num) => {
  if (num === null || num === undefined) return '-';
  return `$${num.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const getQuality = (row) => {
  if (row?.quality) return row.quality;
  if (!row?.note) return 'verified';
  const note = row.note.toLowerCase();
  if (note.includes('incorrect') || note.includes('issue') || note.includes('suspended') || note.includes('api')) {
    return 'known_reporting_issue';
  }
  return 'unverified';
};

const getQualityClass = (quality) => {
  if (quality === 'verified') return 'bg-green-500/10 text-green-300 border-green-500/30';
  if (quality === 'missing') return 'bg-gray-700 text-gray-300 border-gray-600';
  if (quality === 'estimated') return 'bg-blue-500/10 text-blue-300 border-blue-500/30';
  return 'bg-yellow-500/10 text-yellow-300 border-yellow-500/30';
};

const getRevenueValue = (row, metric) => {
  if (!row) return null;
  switch (metric) {
    case 'grossRevenue':
      return row.overall;
    case 'netRevenue':
      return row.vipTotal;
    case 'subscriptionRevenue':
      return row.vipSubs;
    case 'tips':
      return row.vipTips;
    case 'ppvMessages':
    case 'messageRevenue':
      return row.vipMessages;
    case 'renewals':
      return row.vipSubs;
    default:
      return row.vipMessages;
  }
};

const getRevenueChange = (current, previous) => {
  if (!current || !previous || previous.overall === 0) return 'N/A';
  const change = ((current.overall - previous.overall) / previous.overall) * 100;
  return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
};

const platformConfigs = [
  {
    key: 'reddit',
    label: 'Reddit',
    color: '#FB923C',
    metricLabel: 'Impressions',
    metric: (row) => row.reddit?.impressions,
    detail: (row) => `${row.reddit?.posts || 0} posts, ${formatWholeNumber(row.reddit?.upvotes)} upvotes`
  },
  {
    key: 'x',
    label: 'X / Twitter',
    color: '#38BDF8',
    metricLabel: 'Impressions',
    metric: (row) => row.x?.impressions,
    detail: (row) => `${formatWholeNumber(row.x?.profileVisits)} profile visits, ${formatWholeNumber(row.x?.engagements)} engagements`
  },
  {
    key: 'reels',
    label: 'Instagram / Reels',
    color: '#EC4899',
    metricLabel: 'Views',
    metric: (row) => row.reels?.views,
    detail: (row) => `${row.reels?.posted || 0} reels, ${formatWholeNumber(row.reels?.likes)} likes`
  },
  {
    key: 'threads',
    label: 'Threads',
    color: '#60A5FA',
    metricLabel: 'Followers',
    metric: (row) => row.threads?.totalFollowers,
    detail: (row) => `${row.threads?.posts || 0} posts, +${formatWholeNumber(row.threads?.followersGained)} followers`
  },
  {
    key: 'tiktok',
    label: 'TikTok',
    color: '#A78BFA',
    metricLabel: 'Reach',
    metric: () => null,
    detail: () => 'No TikTok data yet'
  }
];

function QualityBadge({ quality }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${getQualityClass(quality)}`}>
      {quality === 'verified' ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
      {dataQualityLabels[quality] || quality}
    </span>
  );
}

function EmptyState({ children }) {
  return (
    <div className="rounded-lg border border-dashed border-gray-700 bg-gray-900 p-8 text-center text-sm text-gray-500">
      <CircleDashed size={24} className="mx-auto mb-2 text-gray-600" />
      {children}
    </div>
  );
}

function AgencyAnalytics({ allData, allMarketingData, analyticsDataStatus }) {
  const [selectedWeeks, setSelectedWeeks] = useState('12');
  const [revenueMetric, setRevenueMetric] = useState('messageRevenue');
  const [overlays, setOverlays] = useState({
    reddit: true,
    x: true,
    reels: true,
    threads: true,
    tiktok: true
  });

  const weekCount = selectedWeeks === 'all' ? null : Number(selectedWeeks);
  const revenueRows = weekCount ? allData.revenue.slice(-weekCount) : allData.revenue;
  const marketingRows = weekCount ? allMarketingData.slice(-weekCount) : allMarketingData;

  const revenueByWeek = useMemo(() => {
    const byWeek = new Map();
    allData.revenue.forEach((row) => {
      byWeek.set(row.week, row);
      byWeek.set(row.period, row);
    });
    return byWeek;
  }, [allData.revenue]);

  const combinedMarketingRows = marketingRows.map((row) => {
    const revenue = revenueByWeek.get(row.week) || revenueByWeek.get(row.period);
    return {
      ...row,
      revenue,
      revenueOverlay: getRevenueValue(revenue, revenueMetric),
      quality: getQuality(row)
    };
  });

  const latestRevenue = revenueRows[revenueRows.length - 1];
  const previousRevenue = revenueRows[revenueRows.length - 2];
  const latestMarketing = marketingRows[marketingRows.length - 1];
  const qualityWarnings = combinedMarketingRows.filter((row) => row.quality !== 'verified');

  const toggleOverlay = (key) => {
    setOverlays((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderPlatformChart = (config) => {
    const rows = combinedMarketingRows
      .filter((row) => config.key === 'tiktok' ? false : row[config.key])
      .map((row) => ({
        period: row.period,
        metric: config.metric(row),
        revenue: row.revenueOverlay,
        detail: config.detail(row),
        quality: row.quality,
        note: row.note
      }));

    if (rows.length === 0) {
      return (
        <div key={config.key} className="chart-card bg-gray-900 rounded-xl p-6 border border-gray-800">
          <div className="mobile-panel-header flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ color: config.color }}>{config.label}</h3>
            <QualityBadge quality="missing" />
          </div>
          <EmptyState>{config.label} data not available yet.</EmptyState>
        </div>
      );
    }

    const hasWarning = rows.some((row) => row.quality !== 'verified');

    return (
      <div key={config.key} className="chart-card bg-gray-900 rounded-xl p-6 border border-gray-800">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold" style={{ color: config.color }}>{config.label}</h3>
            <p className="text-xs text-gray-500">{config.metricLabel} with optional revenue overlay</p>
          </div>
          <div className="mobile-select-row flex items-center gap-3">
            {hasWarning && <QualityBadge quality="known_reporting_issue" />}
            <label className="inline-flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={overlays[config.key]}
                onChange={() => toggleOverlay(config.key)}
                className="accent-purple-500"
              />
              Show revenue overlay
            </label>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="period" stroke="#9CA3AF" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
            <YAxis yAxisId="metric" stroke="#9CA3AF" tick={{ fontSize: 12 }} />
            {overlays[config.key] && <YAxis yAxisId="revenue" orientation="right" stroke="#34D399" tick={{ fontSize: 12 }} />}
            <Tooltip
              contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
              formatter={(value, name) => name === 'Revenue' ? formatMoney(value) : formatWholeNumber(value)}
              labelFormatter={(label) => label}
            />
            <Legend />
            <Bar yAxisId="metric" dataKey="metric" name={config.metricLabel} fill={config.color} radius={[2, 2, 0, 0]} />
            {overlays[config.key] && (
              <Line yAxisId="revenue" type="monotone" dataKey="revenue" name="Revenue" stroke="#34D399" strokeWidth={3} dot={{ r: 3 }} connectNulls />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const correlationRows = revenueRows.map((revenue, index) => {
    const marketing = allMarketingData.find((row) => row.week === revenue.week || row.period === revenue.period);
    const previous = revenueRows[index - 1];

    return {
      period: revenue.period,
      redditReach: marketing?.reddit?.impressions,
      xReach: marketing?.x?.impressions,
      reelsReach: marketing?.reels?.views,
      threadsReach: marketing?.threads?.totalFollowers,
      profileVisits: marketing?.x?.profileVisits,
      totalRevenue: revenue.overall,
      messageRevenue: revenue.vipMessages,
      change: getRevenueChange(revenue, previous),
      quality: getQuality(marketing),
      note: marketing?.note || ''
    };
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Agency analytics</p>
          <h2 className="text-2xl font-serif text-white mb-1">Vasia performance dashboard</h2>
          <p className="text-gray-400 text-sm max-w-3xl">
            Analytics-only view with marketing/revenue correlation, chart overlays, data quality flags, and attribution placeholders for future agency reporting.
          </p>
        </div>
        <div className="mobile-select-row flex flex-wrap gap-3">
          <select
            value={selectedWeeks}
            onChange={(event) => setSelectedWeeks(event.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
          >
            <option value="4">4 weeks</option>
            <option value="8">8 weeks</option>
            <option value="12">12 weeks</option>
            <option value="26">26 weeks</option>
            <option value="all">All weeks</option>
          </select>
          <select
            value={revenueMetric}
            onChange={(event) => setRevenueMetric(event.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
          >
            {revenueMetricOptions.map((option) => (
              <option key={option.key} value={option.key}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className={`rounded-xl border p-4 text-sm ${
        analyticsDataStatus?.error
          ? 'border-yellow-500/30 bg-yellow-500/10 text-yellow-100'
          : analyticsDataStatus?.liveRows > 0
            ? 'border-green-500/30 bg-green-500/10 text-green-100'
            : 'border-gray-800 bg-gray-900 text-gray-400'
      }`}>
        {analyticsDataStatus?.error
          ? `Live API data could not be loaded, so these charts are showing the historical dashboard dataset: ${analyticsDataStatus.error}`
          : analyticsDataStatus?.liveRows > 0
            ? `${analyticsDataStatus.liveRows} weekly rows are coming from Supabase/API sync and are merged into the historical charts.`
            : 'No synced API rows yet. Connect Instagram, X or Reddit in Data Connections to begin updating these charts.'}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-900 rounded-xl p-5 border border-gray-800">
          <p className="text-sm text-gray-400">Latest Total Revenue</p>
          <p className="text-2xl font-bold text-green-400">{formatMoney(latestRevenue?.overall)}</p>
          <p className="text-xs text-gray-500 mt-1">{latestRevenue?.period}</p>
        </div>
        <div className="bg-gray-900 rounded-xl p-5 border border-gray-800">
          <p className="text-sm text-gray-400">Message Revenue</p>
          <p className="text-2xl font-bold text-purple-400">{formatMoney(latestRevenue?.vipMessages)}</p>
          <p className="text-xs text-gray-500 mt-1">Default overlay metric</p>
        </div>
        <div className="bg-gray-900 rounded-xl p-5 border border-gray-800">
          <p className="text-sm text-gray-400">Revenue Change</p>
          <p className={`text-2xl font-bold ${latestRevenue?.overall >= previousRevenue?.overall ? 'text-green-400' : 'text-red-400'}`}>
            {getRevenueChange(latestRevenue, previousRevenue)}
          </p>
          <p className="text-xs text-gray-500 mt-1">Week over week</p>
        </div>
        <div className="bg-gray-900 rounded-xl p-5 border border-gray-800">
          <p className="text-sm text-gray-400">Latest Marketing Week</p>
          <p className="text-2xl font-bold text-blue-400">{latestMarketing?.period || '-'}</p>
          <p className="text-xs text-gray-500 mt-1">{qualityWarnings.length} flagged report rows in range</p>
        </div>
      </div>

      <div className="chart-card bg-gray-900 rounded-xl p-6 border border-gray-800">
        <h3 className="text-lg font-semibold mb-4">Revenue trend</h3>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={revenueRows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="period" stroke="#9CA3AF" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
            <YAxis stroke="#9CA3AF" tick={{ fontSize: 12 }} />
            <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }} formatter={(value) => formatMoney(value)} />
            <Legend />
            <Line type="monotone" dataKey="overall" name="Total Revenue" stroke="#34D399" strokeWidth={3} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="vipMessages" name="Message Revenue" stroke="#A78BFA" strokeWidth={2} dot={{ r: 2 }} />
            <Line type="monotone" dataKey="vipSubs" name="Subscription Revenue" stroke="#60A5FA" strokeWidth={2} dot={{ r: 2 }} />
            <Line type="monotone" dataKey="vipTips" name="Tips" stroke="#FBBF24" strokeWidth={2} dot={{ r: 2 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {platformConfigs.map(renderPlatformChart)}
      </div>

      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-800">
          <h3 className="text-lg font-semibold">Weekly marketing and revenue correlation</h3>
          <p className="text-xs text-gray-500 mt-1">Use this to compare reach/profile visits against OF revenue and message revenue week by week.</p>
        </div>
        <div className="table-scroll overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left px-4 py-3 text-gray-400 font-medium">Week</th>
                <th className="text-right px-4 py-3 text-gray-400 font-medium">Reddit Reach</th>
                <th className="text-right px-4 py-3 text-gray-400 font-medium">X Reach</th>
                <th className="text-right px-4 py-3 text-gray-400 font-medium">Profile Visits</th>
                <th className="text-right px-4 py-3 text-gray-400 font-medium">Reels Views</th>
                <th className="text-right px-4 py-3 text-gray-400 font-medium">Threads Followers</th>
                <th className="text-right px-4 py-3 text-gray-400 font-medium">Total Revenue</th>
                <th className="text-right px-4 py-3 text-gray-400 font-medium">Message Revenue</th>
                <th className="text-right px-4 py-3 text-gray-400 font-medium">WoW</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium">Quality</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium">Notes</th>
              </tr>
            </thead>
            <tbody>
              {correlationRows.map((row) => (
                <tr key={row.period} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                  <td className="px-4 py-3 font-medium">{row.period}</td>
                  <td className="text-right px-4 py-3 text-orange-300">{formatWholeNumber(row.redditReach)}</td>
                  <td className="text-right px-4 py-3 text-sky-300">{formatWholeNumber(row.xReach)}</td>
                  <td className="text-right px-4 py-3">{formatWholeNumber(row.profileVisits)}</td>
                  <td className="text-right px-4 py-3 text-pink-300">{formatWholeNumber(row.reelsReach)}</td>
                  <td className="text-right px-4 py-3 text-blue-300">{formatWholeNumber(row.threadsReach)}</td>
                  <td className="text-right px-4 py-3 text-green-400">{formatMoney(row.totalRevenue)}</td>
                  <td className="text-right px-4 py-3 text-purple-300">{formatMoney(row.messageRevenue)}</td>
                  <td className={`text-right px-4 py-3 ${String(row.change).startsWith('-') ? 'text-red-400' : 'text-green-400'}`}>{row.change}</td>
                  <td className="px-4 py-3"><QualityBadge quality={row.quality} /></td>
                  <td className="px-4 py-3 text-xs text-yellow-300 min-w-[220px]">{row.note || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
        <h3 className="text-lg font-semibold mb-2">Source-level attribution and conversion</h3>
        <p className="text-sm text-gray-400 mb-4">
          This section is ready for future agency data: clicks, OF visitors, subscribers, buyers, message revenue, conversion rate, revenue per visitor, and traffic/revenue split by source.
        </p>

        {channelAttributionData.length === 0 ? (
          <EmptyState>Attribution data not available yet.</EmptyState>
        ) : (
          <div className="table-scroll overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left px-4 py-3 text-gray-400 font-medium">Week</th>
                  <th className="text-left px-4 py-3 text-gray-400 font-medium">Source</th>
                  <th className="text-right px-4 py-3 text-gray-400 font-medium">Clicks</th>
                  <th className="text-right px-4 py-3 text-gray-400 font-medium">OF Visitors</th>
                  <th className="text-right px-4 py-3 text-gray-400 font-medium">Subs</th>
                  <th className="text-right px-4 py-3 text-gray-400 font-medium">Buyers</th>
                  <th className="text-right px-4 py-3 text-gray-400 font-medium">Message Revenue</th>
                  <th className="text-right px-4 py-3 text-gray-400 font-medium">Conversion</th>
                </tr>
              </thead>
              <tbody>
                {channelAttributionData.map((row) => (
                  <tr key={row.week + row.source} className="border-b border-gray-800/50">
                    <td className="px-4 py-3">{row.week}</td>
                    <td className="px-4 py-3">{row.source}</td>
                    <td className="text-right px-4 py-3">{formatWholeNumber(row.clicks)}</td>
                    <td className="text-right px-4 py-3">{formatWholeNumber(row.ofVisitors)}</td>
                    <td className="text-right px-4 py-3">{formatWholeNumber(row.subscribers)}</td>
                    <td className="text-right px-4 py-3">{formatWholeNumber(row.buyers)}</td>
                    <td className="text-right px-4 py-3">{formatMoney(row.messageRevenue)}</td>
                    <td className="text-right px-4 py-3">{row.conversionRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AgencyAnalytics;
